import { Alert, Button, Card, Col, Form, message, Row, Space, Spin, Tooltip, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import TitleSection from "../../components/TitleSection";
import { httpRequest } from "../../helpers/api";
import { getErrorMessage } from "../../helpers/errorHandler";
import useBreadcrumbs from "../../hooks/useBreadcrumbs";
import { BaseResponsePaginationProps, BaseResponseProps } from "../../types/config.type";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { UserProperties } from "../../types/user.type";
import EmployeeInformation from "../transferEmployee/CreateComponents/EmployeeInformation";
import ListingCard from "../transferEmployee/CreateComponents/ListingCard";
import { EListingStatus, ListingProperties } from "../../types/listing.type";
import { handleSubmitFailed } from "../../helpers/formRules";
import { successModal } from "../../helpers/modal";
import { TransferListingDataItem } from "../../types/transferEmployee.type";
import { EEmployeeStatus } from "../../types/employee.type";

const { Title } = Typography;

export interface TransferListingItem extends ListingProperties {
	checked: boolean;
	agent: {
		name: string;
		officeName?: string;
		userId?: string;
		employeeId?: string;
		officeId?: string;
		profilePic?: string;
	};
}
export interface TransferFormValues {
	userListing?: TransferListingItem[];
	transferListing?: TransferListingItem[];
}

interface Params {
	userId?: string;
}
const CreateTransferListing: React.FC = () => {
	const { setBreadcrumb } = useBreadcrumbs();
	const [isLoading, setIsLoading] = useState(false);
	const [isActionLoading, setIsActionLoading] = useState(false);
	const [user, setUser] = useState<UserProperties>();
	const { userId } = useParams<Params>();
	const [form] = Form.useForm<TransferFormValues>();
	const history = useHistory();
	const transferListingWatch = Form.useWatch("transferListing", form);

	useEffect(() => {
		(async () => {
			setIsLoading(true);
			try {
				let { data } = await httpRequest.get<BaseResponseProps<UserProperties>>("/users/" + userId, {
					params: {
						includeData: "totalCommission,employeeOffice",
						filterStatus: EEmployeeStatus.ACTIVE,
					},
				});
				let bc = [
					{
						label: data.payload?.nickName?.toUpperCase(),
						origin: data.payload.employee?.[0].employeeId,
					},
				];
				let user = data.payload;
				const activeEmployee = (data.payload?.employee ?? []).filter(
					(v) => v.status === EEmployeeStatus.ACTIVE
				);
				user = {
					...user,
					employee: activeEmployee,
				};

				let url =
					"listings" +
					"?filterUserId=" +
					user.userId +
					"&limit=9999999999999" +
					"&includeData=listingPart,listingCertificateType,listingPropertyType,listingRenewHistory,listingTransferHistory" +
					"&filterStatus=" +
					Object.keys(EListingStatus)
						.filter((status) => status !== EListingStatus.CLOSED)
						.join(",");
				let { data: resListings } = await httpRequest.get<BaseResponsePaginationProps<ListingProperties>>(url);
				let listings: TransferListingItem[] = resListings.payload.results.map((item) => ({
					...item,
					checked: false,
					agent: {
						name: user.nickName!,
						userId: item.userId,
						employeeId: item.employeeId,
						officeId: item.officeId,
					},
				}));
				form.setFieldValue("userListing", listings);
				setBreadcrumb(bc);
				setUser(user);
			} catch (error) {
				message.error(getErrorMessage(error));
			}
			setIsLoading(false);
		})();

		// eslint-disable-next-line
	}, [userId]);

	const handleSubmit = async (values: TransferFormValues) => {
		setIsActionLoading(true);
		try {
			let filteredLength =
				values.transferListing?.filter(
					(row) => row.listingId && row.agent.officeId && row.agent.userId && row.agent.employeeId
				) ?? [];

			if (filteredLength.length !== (values.transferListing ?? []).length) {
				return message.error("Something went wrong, transfer listing data not same!");
			}
			let transferListingData: TransferListingDataItem[] =
				filteredLength.map((item) => ({
					listingId: item.listingId ?? "",
					newOfficeId: item.agent.officeId ?? "",
					newUserId: item.agent.userId ?? "",
					newEmployeeId: item.agent.employeeId ?? "",
					// -- listinData moved to backend
					// listingData: {
					// 	...item,
					// 	agent: undefined,
					// 	checked: undefined,
					// },
					newUserData: {
						name: item.agent.name,
						officeName: item.agent.officeName,
						profilePic: item.agent.profilePic,
					},
				})) ?? [];

			let metadata = {
				// keepListingData: values.userListing ?? [],
				keepListingData: (values.userListing ?? []).map(item => ({
					listingId: item.listingId ?? "",
					checked: item.checked,
					agent: item.agent,
				})),
				transferListingData,
				originUserData: {
					name: user?.nickName,
					profilePic: user?.profilePic,
					existingRole: user?.employee?.[0]?.employeeRole?.map((row) => row.role),
				},
			};

			const dto = {
				originListingUserId: user?.userId,
				originListingOfficeId: user?.employee?.[0]?.officeId,
				originListingEmployeeId: user?.employee?.[0]?.employeeId,
				transferListingItems: (values?.transferListing ?? []).map((v) => ({
					listingId: v.listingId,
					toListingUserId: v.agent.userId,
					toListingOfficeId: v.agent.officeId,
					toListingEmployeeId: v.agent.employeeId,
				})),
				metadata,
			};

			await httpRequest.post("transfer-listings", dto);

			form.resetFields();

			successModal({
				title: "Sucessfully Transfer Listing",
				subtitle: "You have successfully transfer listing",
				onOk: () => {
					history.push("/transfer-listing");
				},
			});
		} catch (error) {
			message.error(getErrorMessage(error));
		} finally {
			setIsActionLoading(false);
		}
	};

	return (
		<React.Fragment>
			<TitleSection
				icon="back"
				title="Transfer Listing for Inactive Agent"
				rightAction={
					<Tooltip
						title={
							!transferListingWatch || (transferListingWatch || []).length === 0
								? "Transfer Listing can't be empty"
								: ""
						}
					>
						<Button
							onClick={form.submit}
							type="primary"
							disabled={!transferListingWatch || (transferListingWatch || []).length === 0}
							loading={isActionLoading}
						>
							Transfer Now
						</Button>
					</Tooltip>
				}
			/>
			<Alert
				className="warning-watermark"
				message="This agent is currently inactive. You can transfer all listing to another active agent in the same office."
				type="warning"
				showIcon
				icon={<ExclamationCircleOutlined />}
				style={{
					marginBottom: 8,
				}}
			/>

			<Space
				direction="vertical"
				size="large"
			>
				<Card
					bordered={false}
					size="small"
				>
					<Row>
						<Col span={12}>
							<Title
								level={3}
								style={{ marginBottom: 0 }}
							>
								Agent Info
							</Title>
							<EmployeeInformation
								user={user}
								isSmall
								isTransferListing
							/>
						</Col>
					</Row>
				</Card>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSubmit}
					onFinishFailed={handleSubmitFailed}
				>
					<Spin spinning={isLoading}>
						<ListingCard
							isTransferListing={true}
							officeId={user?.employee?.[0]?.officeId}
							employeeId={user?.employee?.[0]?.employeeId}
							user={user}
							form={form}
						/>
					</Spin>
				</Form>
			</Space>
		</React.Fragment>
	);
};
export default CreateTransferListing;
